import React, { useState } from "react";
import { ROUTES } from "../../../constants/routes-path";
import { processesService } from "../../../services/processesService";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Stack,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import Loading from "../../../components/Loading/Loading";
import { generatePrintNpsTrialHtml } from "./ProcessPrintNpsTrial";
import { configService } from "services/configService";
import DateTime from "react-datetime";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";
import { checkErrors, hasError } from "utils/forms-utils";

const PrintNpsTrial = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const textColor = useColorModeValue("gray.700", "white");
    const translationKey = "process";
    const formTranslationKey = `${translationKey}.form`;
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        trialDate: {
            value: "",
            validations: [{ required: true }],
        },
    });
    const [inputError, setInputError] = useState({});

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes);
    };

    const handleTrialDateChange = (value) => {
        const m = moment(value);
        const date = m.isValid() ? m.format(DATE_FORMAT_ISO) : "";

        const updatedData = {
            ...formData,
            trialDate: {
                ...formData.trialDate,
                value: date,
            },
        };

        setFormData(updatedData);

        setInputError(
            checkErrors({
                formData: updatedData,
                inputError,
                name: "trialDate"
            })
        );
    };

    const handlePrintNPsForTrial = async () => {
        setIsLoading(true);
        try {
            const date = formData.trialDate.value.split('-').reverse().join('/');
            const responseData = await processesService.printNpsTrial(date);
            const config = await configService.get();
            const html = await generatePrintNpsTrialHtml(responseData, config);
            const newWindow = window.open('', '_blank');
            newWindow.document.write(html);
            newWindow.document.close();
        } catch (error) {
            showToast({
                title: t(error),
                status: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
            <Card>
                <CardHeader mb="20px">
                    <Text color={textColor} fontSize="lg" fontWeight="bold">
                        {t(`${formTranslationKey}.title.printing-data`)}
                    </Text>
                </CardHeader>
                <CardBody>
                    {isLoading ? (
                        <Loading />
                    ) : (
                        <>
                            <FormControl isRequired isInvalid={inputError["trialDate"]}>
                                <FormLabel fontSize="xs" fontWeight="bold">
                                    {t(`${formTranslationKey}.fields.trial-date.label`)}
                                </FormLabel>
                                <DateTime
                                    dateFormat={DATE_FORMAT}
                                    timeFormat={false}
                                    locale={LOCALE_PT_BR}
                                    value={
                                        formData.trialDate.value
                                            ? moment(formData.trialDate.value, DATE_FORMAT_ISO)
                                            : ""
                                    }
                                    renderInput={(props) => (
                                        <Input {...props} fontSize="sm" variant="main" placeholder={t(`${formTranslationKey}.fields.trial-date.placeholder`)} />
                                    )}
                                    onChange={handleTrialDateChange}
                                />
                                <FormErrorMessage>{t(inputError["trialDate"])}</FormErrorMessage>
                            </FormControl>
                            <Stack mt={"20px"} w="100%" direction="row" justify="space-between">
                                <Button w="125px"
                                    variant="no-effects"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={redirectToList}
                                >
                                    <Text>
                                        {t("system.actions.cancel")}
                                    </Text>
                                </Button>

                                <Button
                                    w="125px"
                                    variant="dark"
                                    fontSize="sm"
                                    isDisabled={hasError({ formData, inputError })}
                                    onClick={handlePrintNPsForTrial}
                                >
                                    <Text>
                                        {t("system.actions.print")}
                                    </Text>
                                </Button>
                            </Stack>
                        </>
                    )}
                </CardBody>
            </Card>
        </Flex>
    );

};

export default PrintNpsTrial;